/* istanbul ignore file */
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Flex, Heading, Radio, RadioGroup, Spinner, Stack, Text } from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import AuthConsentNotice from 'auth/AuthConsentNotice';

import { useSystemTranslation } from 'hooks';
import { useApplicationSettings } from 'hooks/useApplicationSettings';
import { getPropertyApplications } from 'services';

export interface Version {
  updatedDate: string;
}

export interface PropertyApplication {
  id: string;
  name: string;
  description: string;
  versions: Version[];
}

interface Props {
  applicationSelected: any;
  setSelectedApplicationId: (id: string) => void;
  currentApplicationId?: string;
}
export function SelectApplication(props: Props) {
  const { applicationSelected, setSelectedApplicationId, currentApplicationId } = props;
  const [error, setError] = React.useState('');

  const { tSystem } = useSystemTranslation();
  const { updateSettings } = useApplicationSettings();

  const { orgId, propertyId } = useParams();

  const { data: applications, isPending } = useQuery({
    queryKey: ['select-application-property-applications'],
    queryFn: () => getPropertyApplications(Number(orgId), Number(propertyId), true),
  });

  const sortedApplications = applications?.data.sort(
    (a: PropertyApplication, b: PropertyApplication) => {
      const mostRecentDateA = a.versions.reduce((prevDate, currVersion) => {
        const currentDate = new Date(currVersion.updatedDate);
        return currentDate > prevDate ? currentDate : prevDate;
      }, new Date(0));

      const mostRecentDateB = b.versions.reduce((prevDate, currVersion) => {
        const currentDate = new Date(currVersion.updatedDate);
        return currentDate > prevDate ? currentDate : prevDate;
      }, new Date(0));

      return mostRecentDateB.getTime() - mostRecentDateA.getTime();
    },
  );

  const handleContinue = () => {
    if (!currentApplicationId) {
      setError(tSystem(SystemTranslationKeys.entry.selectApplicationType));
      return;
    }
    applicationSelected();
  };

  const handleApplicationSelected = (applicationId: string) => {
    const selectedApplication = sortedApplications?.find(
      (application: any) => application.id === applicationId,
    );
    setSelectedApplicationId(applicationId);
    updateSettings(selectedApplication?.settings ?? {});
    setError('');
  };

  if (isPending) return <Spinner />;

  return (
    <Stack spacing={8} width="100%" maxW="360px">
      <Heading fontSize="xl">{tSystem(SystemTranslationKeys.entry.selectApplicationType)}</Heading>
      <RadioGroup onChange={handleApplicationSelected} value={currentApplicationId}>
        <Stack spacing={4}>
          {sortedApplications?.map((application: any) => (
            <Flex key={`select-application-${application.id}`} direction="column">
              <Radio size="lg" value={application.id} alignItems="flex-start">
                <Text fontSize="md" fontWeight="bold">
                  {application.name}
                </Text>
              </Radio>
              <Text marginLeft={8} fontSize="sm" color="gray.600">
                {application.description}
              </Text>
            </Flex>
          ))}
        </Stack>
      </RadioGroup>
      <AuthConsentNotice />
      {error && <Text color="red.500">{error}</Text>}
      <Button p={8} w="100%" variant="brand" onClick={handleContinue}>
        {tSystem(SystemTranslationKeys.entry.continueButton)}
      </Button>
    </Stack>
  );
}
